interface PersistedRowState {
  quantity: string;
  size?: string;
}

export interface PersistedOrderGuideState {
  timestamp: number;
  version: number;
  deliveryDate?: string;
  rows: { [skuVariantKey: string]: PersistedRowState };
}

export interface OrderGuideStateInput {
  deliveryDate?: string;
  rows: { [skuVariantKey: string]: PersistedRowState };
}

const STORAGE_PREFIX = "orderGuide";
const TTL_HOURS = 12;
const CURRENT_VERSION = 2;

function storageKey(orderGuideId: string): string {
  return `${STORAGE_PREFIX}:${orderGuideId}:state`;
}

export class OrderGuidePersistenceService {
  static save(orderGuideId: string, state: OrderGuideStateInput): boolean {
    if (!orderGuideId) return false;

    try {
      const persisted: PersistedOrderGuideState = {
        ...state,
        timestamp: Date.now(),
        version: CURRENT_VERSION,
      };
      localStorage.setItem(storageKey(orderGuideId), JSON.stringify(persisted));
      return true;
    } catch (error) {
      console.warn(
        `Failed to save order guide state for ${orderGuideId}:`,
        error,
      );
      return false;
    }
  }

  static load(orderGuideId: string): PersistedOrderGuideState | null {
    if (!orderGuideId) return null;

    try {
      const raw = localStorage.getItem(storageKey(orderGuideId));
      if (!raw) return null;

      const state: PersistedOrderGuideState = JSON.parse(raw);

      if (state.version !== CURRENT_VERSION) {
        this.clear(orderGuideId);
        return null;
      }

      const ageInHours = (Date.now() - state.timestamp) / (1000 * 60 * 60);
      if (ageInHours > TTL_HOURS) {
        this.clear(orderGuideId);
        return null;
      }

      return state;
    } catch (error) {
      console.warn(
        `Failed to load order guide state for ${orderGuideId}:`,
        error,
      );
      return null;
    }
  }

  static clear(orderGuideId: string): void {
    if (!orderGuideId) return;

    try {
      localStorage.removeItem(storageKey(orderGuideId));
    } catch (error) {
      console.warn(
        `Failed to clear order guide state for ${orderGuideId}:`,
        error,
      );
    }
  }
}

export default OrderGuidePersistenceService;
